"use client";

import dynamic from "next/dynamic";

const Dashboard = dynamic(() => import("@/components/dashboard-page"));

import ShareOutputActions from "@/components/share-output-actions";

import { FormEvent, useEffect, useRef, useState } from "react";
import {
  User,
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  browserPopupRedirectResolver,
  setPersistence,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db, googleProvider } from "@/lib/firebase";
import WelcomeCard from "@/components/welcome-card";
import GlowAtWork from "@/components/glow-at-work";
import GlowBusinessMap from "@/components/glow-business-map";
import GlowOrb from "@/components/glow-orb";
import GlowResearchProgress from "@/components/glow-research-progress";
import GlowActivityStream from "@/components/glow-activity-stream";
import LandingWowExperience from "@/components/landing-wow-experience";
import Footer from "@/components/footer";
import StaleVersionGuard from "@/components/stale-version-guard";
import WorkContinuity from "@/components/work-continuity";
import { getCheckoutAnalyticsContext, trackEvent } from "@/lib/analytics";

const TESTER_MODE = process.env.NEXT_PUBLIC_TESTER_MODE === "true";

const PRO_SCAN_STAGES = [
  {
    title: "Learning your business",
    detail:
      "Using your saved Free Business Check-up, business profile, priorities and Scan brief.",
  },
  {
    title: "Reviewing your Google presence",
    detail:
      "Reviewing your website, Google listing and available public growth signals.",
  },
  {
    title: "Finding margin opportunities",
    detail:
      "Looking across revenue, costs, waste and operational opportunities.",
  },
  {
    title: "Preparing practical outputs",
    detail:
      "Drafting useful actions and agent-prepared work for your approval.",
  },
  {
    title: "Building your 30-day Action Plan",
    detail:
      "Ordering the strongest actions by likely value, urgency and effort.",
  },
  {
    title: "Finalising your Pro Scan",
    detail: "Checking the recommendations and preparing your saved result.",
  },
] as const;

function formatAiFootprint(aiFootprintKg: number): string {
  if (!Number.isFinite(aiFootprintKg) || aiFootprintKg <= 0) {
    return "0 g CO2e";
  }

  if (aiFootprintKg < 1) {
    const grams = aiFootprintKg * 1000;

    return `${grams < 10 ? grams.toFixed(1) : grams.toFixed(0)} g CO2e`;
  }

  return `${aiFootprintKg.toFixed(3)} kg CO2e`;
}

function normaliseSavedWebsite(value: unknown): string {
  const website = String(value || "").trim();

  if (!website) {
    return "";
  }

  if (/^https?:\/\//i.test(website)) {
    return website;
  }

  return `https://${website}`;
}

function buildBusinessProfileFromGlowCheck(
  glowCheck: any,
  existingProfile: any = {},
) {
  const existing =
    existingProfile && typeof existingProfile === "object"
      ? existingProfile
      : {};

  const selectedPlace =
    glowCheck?.selectedPlace && typeof glowCheck.selectedPlace === "object"
      ? glowCheck.selectedPlace
      : {};

  const selectedPlaceName =
    typeof selectedPlace?.displayName === "string"
      ? selectedPlace.displayName
      : typeof selectedPlace?.displayName?.text === "string"
        ? selectedPlace.displayName.text
        : "";

  const inheritedProfile: Record<string, string> = {
    displayName: String(
      glowCheck?.businessName || selectedPlaceName || "",
    ).trim(),

    businessType: String(glowCheck?.businessType || "").trim(),

    country: String(glowCheck?.country || "").trim(),

    location: String(
      glowCheck?.addressOrArea ||
        selectedPlace?.formattedAddress ||
        selectedPlace?.address ||
        "",
    ).trim(),

    website: normaliseSavedWebsite(
      glowCheck?.website ||
        selectedPlace?.websiteUri ||
        selectedPlace?.website ||
        "",
    ),

    googleBusinessProfileUrl: String(
      glowCheck?.googleBusinessProfile ||
        selectedPlace?.googleMapsUri ||
        selectedPlace?.googleMapsUrl ||
        selectedPlace?.mapsUrl ||
        "",
    ).trim(),

    businessContext: String(glowCheck?.businessContext || "").trim(),
  };

  const mergedProfile: Record<string, any> = {
    ...existing,
  };

  for (const [field, inheritedValue] of Object.entries(inheritedProfile)) {
    const currentValue = String(mergedProfile[field] || "").trim();

    if (!currentValue && inheritedValue) {
      mergedProfile[field] = inheritedValue;
    }
  }

  return mergedProfile;
}

function formatShareableScanItem(item: any) {
  if (typeof item === "string") {
    return item.trim();
  }

  if (!item || typeof item !== "object") {
    return "";
  }

  return [
    item.title || "",
    item.whyItMatters ? `Why it matters: ${item.whyItMatters}` : "",
    item.firstStep ? `How to start: ${item.firstStep}` : "",
    item.likelyImpact ? `Likely impact: ${item.likelyImpact}` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

function formatProScanForSharing(scan: any) {
  if (!scan) {
    return "";
  }

  const sections = [
    ["Immediate actions", scan.immediateActions],
    ["Google growth work", scan.googleVisibility],
    ["Revenue opportunities", scan.revenueIdeas],
    ["Cost and waste opportunities", scan.costWasteIdeas],
  ];

  const sectionText = sections
    .map(([title, items]) => {
      const formattedItems = (Array.isArray(items) ? items.slice(0, 5) : [])
        .map((item: any, index: number) => {
          const formatted = formatShareableScanItem(item);

          return formatted ? `${index + 1}. ${formatted}` : "";
        })
        .filter(Boolean);

      if (!formattedItems.length) {
        return "";
      }

      return [String(title), ...formattedItems].join("\n\n");
    })
    .filter(Boolean);

  return [
    "MarginGlow Pro Scan",
    scan.headline || "",
    scan.executiveSummary || "",
    ...sectionText,
    "",
    "Prepared with MarginGlow AI.",
  ]
    .filter(Boolean)
    .join("\n\n");
}

function splitPersonName(displayName: string) {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);

  return {
    firstName: parts[0] || "",
    lastName: parts.length > 1 ? parts.slice(1).join(" ") : "",
  };
}

async function saveUser(user: User, isNewUser = false) {
  try {
    const profileData: Record<string, unknown> = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || "",
      photoURL: user.photoURL || "",
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (isNewUser) {
      const personName = splitPersonName(user.displayName || "");

      profileData.accountCreatedAt = serverTimestamp();

      if (personName.firstName) {
        profileData.firstName = personName.firstName;
      }

      if (personName.lastName) {
        profileData.lastName = personName.lastName;
      }

      if (personName.firstName && personName.lastName) {
        profileData.nameConfirmedAt = serverTimestamp();
      }
    }

    await setDoc(doc(db, "users", user.uid), profileData, { merge: true });

    return true;
  } catch (error) {
    console.error(
      "Signed in successfully, but the MarginGlow user profile could not be updated:",
      error,
    );

    return false;
  }
}

async function transferPendingGlowCheck(user: User): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  const storageKey = "marginglow_pending_glow_check";
  const storedGlowCheck = window.localStorage.getItem(storageKey);

  if (!storedGlowCheck) {
    return false;
  }

  let parsedGlowCheck: any;

  try {
    parsedGlowCheck = JSON.parse(storedGlowCheck);
  } catch (error) {
    console.error("Pending Free Business Check-up was invalid:", error);
    window.localStorage.removeItem(storageKey);
    return false;
  }

  if (!parsedGlowCheck?.result) {
    window.localStorage.removeItem(storageKey);
    return false;
  }

  const userRef = doc(db, "users", user.uid);
  const userSnapshot = await getDoc(userRef);
  const userData = userSnapshot.data();

  const currentAiFootprintKg =
    typeof userData?.aiFootprintKg === "number" ? userData.aiFootprintKg : 0;

  const checkupAiFootprintKg =
    typeof parsedGlowCheck.aiFootprintKgEstimate === "number"
      ? parsedGlowCheck.aiFootprintKgEstimate
      : 0.0001;

  const inheritedBusinessProfile = buildBusinessProfileFromGlowCheck(
    parsedGlowCheck,
    userData?.businessProfile,
  );

  await setDoc(
    userRef,
    {
      latestGlowCheck: parsedGlowCheck,
      latestGlowCheckSavedAt: serverTimestamp(),
      businessProfile: inheritedBusinessProfile,
      ...(parsedGlowCheck?.result?.businessMap
        ? { glowBusinessMap: parsedGlowCheck.result.businessMap }
        : {}),
      aiFootprintKg: currentAiFootprintKg + checkupAiFootprintKg,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  window.localStorage.removeItem(storageKey);

  return true;
}

type CheckoutPlan = "instant_glow_scan" | "glow_starter";

async function startCheckout(
  plan: CheckoutPlan,
  userId?: string,
  businessName?: string,
  powerCode?: string,
): Promise<boolean> {
  try {
    if (!auth.currentUser) {
      throw new Error("Please sign in before starting checkout.");
    }

    const firebaseIdToken = await auth.currentUser.getIdToken();
    const analytics = await getCheckoutAnalyticsContext();

    const response = await fetch("/api/create-checkout-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${firebaseIdToken}`,
      },
      body: JSON.stringify({
        plan,
        userId,
        businessName,
        powerCode:
          typeof powerCode === "string" ? powerCode.trim().toUpperCase() : "",
        analytics,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.url) {
      throw new Error(data.error || "Could not start checkout.");
    }

    trackEvent("begin_checkout", {
      items: [
        {
          item_id: plan,
          item_name:
            plan === "instant_glow_scan"
              ? "MarginGlow Pro Activation"
              : "MarginGlow Starter",
          quantity: 1,
        },
      ],
    });

    window.location.href = data.url;
    return true;
  } catch (error) {
    console.error("Checkout error:", error);
    alert(
      error instanceof Error
        ? error.message
        : "Could not start Stripe Checkout. Please try again.",
    );
    return false;
  }
}

function AuthPanel({
  initialMode = "signup",
  checkoutIntent = false,
  glowCheckReady = false,
}: {
  initialMode?: "signin" | "signup";
  checkoutIntent?: boolean;
  glowCheckReady?: boolean;
}) {
  const [mode, setMode] = useState<"signin" | "signup">(initialMode);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [checkoutPowerCode, setCheckoutPowerCode] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);

  useEffect(() => {
    setMode(initialMode);
    setMessage("");
  }, [initialMode]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setKeepSignedIn(
      window.localStorage.getItem("marginglow_auth_persistence") !== "session",
    );
  }, []);

  async function prepareAuthPersistence() {
    await setPersistence(
      auth,
      keepSignedIn ? browserLocalPersistence : browserSessionPersistence,
    );
  }

  function updateKeepSignedIn(value: boolean) {
    setKeepSignedIn(value);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        "marginglow_auth_persistence",
        value ? "local" : "session",
      );
    }
  }

  useEffect(() => {
    if (!checkoutIntent || typeof window === "undefined") {
      setCheckoutPowerCode("");
      return;
    }

    try {
      const pendingCheckoutRaw = window.localStorage.getItem(
        "marginglow_pending_checkout",
      );

      if (!pendingCheckoutRaw) {
        return;
      }

      const pendingCheckout = JSON.parse(pendingCheckoutRaw);

      setCheckoutPowerCode(
        typeof pendingCheckout?.powerCode === "string"
          ? pendingCheckout.powerCode.replace(/[^a-z]/gi, "").toUpperCase()
          : "",
      );
    } catch (error) {
      console.error("Pending Power Code could not be loaded:", error);
    }
  }, [checkoutIntent]);

  function updateCheckoutPowerCode(value: string) {
    const normalisedCode = value.replace(/[^a-z]/gi, "").toUpperCase();

    setCheckoutPowerCode(normalisedCode);

    if (!checkoutIntent || typeof window === "undefined") {
      return;
    }

    try {
      const pendingCheckoutRaw = window.localStorage.getItem(
        "marginglow_pending_checkout",
      );

      const pendingCheckout = pendingCheckoutRaw
        ? JSON.parse(pendingCheckoutRaw)
        : {
            plan: "instant_glow_scan",
            businessName: businessName.trim(),
            createdAt: new Date().toISOString(),
          };

      window.localStorage.setItem(
        "marginglow_pending_checkout",
        JSON.stringify({
          ...pendingCheckout,
          powerCode: normalisedCode,
        }),
      );
    } catch (error) {
      console.error("Pending Power Code could not be saved:", error);
    }
  }

  async function googleLogin() {
    setBusy(true);
    setMessage("");

    try {
      await prepareAuthPersistence();
      const result = await signInWithPopup(auth, googleProvider, browserPopupRedirectResolver);

      const isNewGoogleUser = getAdditionalUserInfo(result)?.isNewUser === true;

      await saveUser(result.user, isNewGoogleUser);

      try {
        trackEvent(isNewGoogleUser ? "sign_up" : "login", {
          method: "google",
        });
      } catch (postLoginError) {
        console.error(
          "Google authentication succeeded, but post-login tracking failed:",
          postLoginError,
        );
      }
    } catch (error) {
      const errorCode =
        typeof error === "object" && error !== null && "code" in error
          ? String((error as { code?: string }).code)
          : "";

      const errorMessage =
        error instanceof Error ? error.message : String(error || "");

      console.error("Google authentication error:", error);

      if (errorCode === "auth/popup-closed-by-user") {
        setMessage("Google sign-in was closed before it finished.");
      } else if (errorCode === "auth/popup-blocked") {
        setMessage(
          "Your browser blocked the Google sign-in window. Allow pop-ups and try again.",
        );
      } else if (errorCode === "auth/unauthorized-domain") {
        setMessage(
          "This MarginGlow address is not yet authorised for Google sign-in.",
        );
      } else if (
        errorCode === "auth/account-exists-with-different-credential"
      ) {
        setMessage(
          "An account already exists for this email using a password. Sign in with your password first.",
        );
      } else if (errorCode === "auth/operation-not-allowed") {
        setMessage("Google sign-in is not enabled for this Firebase project.");
      } else {
        setMessage(
          `Google authentication failed${
            errorCode ? ` (${errorCode})` : ""
          }${errorMessage ? `: ${errorMessage}` : "."}`,
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function emailLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (mode === "signup" && (!firstName.trim() || !lastName.trim())) {
      setMessage("Please enter your first and last name.");
      return;
    }

    setBusy(true);
    setMessage("");

    try {
      await prepareAuthPersistence();

      const result =
        mode === "signup"
          ? await createUserWithEmailAndPassword(auth, email.trim(), password)
          : await signInWithEmailAndPassword(auth, email.trim(), password);

      if (mode === "signup") {
        await updateProfile(result.user, {
          displayName: [firstName.trim(), lastName.trim()]
            .filter(Boolean)
            .join(" "),
        });
      }

      await saveUser(result.user, mode === "signup");

      try {
        trackEvent(mode === "signup" ? "sign_up" : "login", {
          method: "email",
        });
      } catch (trackingError) {
        console.error(
          "Email authentication succeeded, but tracking failed:",
          trackingError,
        );
      }

      if (mode === "signup") {
        void setDoc(
          doc(db, "businesses", result.user.uid),
          {
            ownerUid: result.user.uid,
            name: businessName || "My business",
            plan: "Free",
            taskCredits: 5,
            weeklyEmailEnabled: false,
            dataLearningEnabled: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        ).catch((profileError) => {
          console.error(
            "Account created, but the initial business record could not be saved:",
            profileError,
          );
        });
      }
    } catch (error) {
      const errorCode =
        typeof error === "object" && error !== null && "code" in error
          ? String((error as { code?: string }).code)
          : "";

      const errorMessage =
        error instanceof Error ? error.message : String(error || "");

      console.error("Email authentication error:", error);

      if (
        errorCode === "auth/invalid-credential" ||
        errorCode === "auth/user-not-found"
      ) {
        setMessage(
          "We could not find an account with those details. Create a new account instead?",
        );
      } else if (errorCode === "auth/email-already-in-use") {
        setMessage(
          "An account already exists for this email. Try signing in instead.",
        );
      } else if (errorCode === "auth/weak-password") {
        setMessage(
          "Please choose a stronger password with at least 6 characters.",
        );
      } else if (errorCode === "auth/invalid-email") {
        setMessage("Please enter a valid email address.");
      } else if (errorCode === "auth/operation-not-allowed") {
        setMessage(
          "Email and password accounts are not enabled for this Firebase project.",
        );
      } else {
        setMessage(
          `Authentication failed${
            errorCode ? ` (${errorCode})` : ""
          }${errorMessage ? `: ${errorMessage}` : "."}`,
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    const cleanEmail = email.trim();

    if (!cleanEmail) {
      setMessage(
        "Enter your email address first, then select Forgot password.",
      );
      return;
    }

    setBusy(true);
    setMessage("");

    try {
      await sendPasswordResetEmail(auth, cleanEmail);
      setMessage(
        "Password reset email sent. Check your inbox and spam folder, then return here to sign in.",
      );

      trackEvent("password_reset_requested", {
        method: "email",
      });
    } catch (error) {
      const errorCode =
        typeof error === "object" && error !== null && "code" in error
          ? String((error as { code?: string }).code)
          : "";

      if (errorCode === "auth/invalid-email") {
        setMessage("Please enter a valid email address.");
      } else if (errorCode === "auth/too-many-requests") {
        setMessage(
          "Too many reset attempts. Please wait a few minutes and try again.",
        );
      } else {
        setMessage(
          "We could not send the reset email. Check the address and try again.",
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-[2rem] border border-[#d7e5df] bg-white p-7 shadow-2xl shadow-[#047b78]/10">
      <h2 className="text-3xl font-light tracking-tight text-[#092723]">
        {mode === "signin" ? "Welcome back" : "Create your account"}
      </h2>

      <p className="mt-2 text-sm leading-6 text-[#5f706c]">
        {checkoutIntent
          ? mode === "signin"
            ? "Sign in to continue to secure checkout."
            : "Create your account to continue to secure checkout."
          : glowCheckReady
            ? mode === "signin"
              ? "Sign in to see your complete Free Business Check-up."
              : "Create your free account to see the complete Check-up and save it in your private dashboard."
            : mode === "signin"
              ? "Sign in to your private MarginGlow workspace."
              : "Start with a Free Business Check-up. No card required."}
      </p>

      {checkoutIntent && (
        <div className="mt-6 rounded-2xl border border-[#ffd2c8] bg-[#fff5f1] p-4">
          <label
            htmlFor="auth-power-code"
            className="block text-sm font-light text-[#092723]"
          >
            Have a Power Code or judge access code?
          </label>

          <input
            id="auth-power-code"
            type="text"
            value={checkoutPowerCode}
            onChange={(event) => updateCheckoutPowerCode(event.target.value)}
            placeholder="Enter Power Code"
            autoComplete="off"
            spellCheck={false}
            className="mt-2 w-full rounded-2xl border border-[#d7e5df] bg-white px-4 py-3 text-sm font-light uppercase tracking-[0.08em] text-[#092723] outline-none transition focus:border-[#007c7a]"
          />

          <p className="mt-2 text-xs font-light leading-5 text-[#52635f]">
            Letters only. Capitalisation does not matter. Enter your Power Code
            or judge access code before continuing. Invited XPRIZE judges
            receive complimentary access without entering payment details.
          </p>

          {checkoutPowerCode && (
            <p className="mt-2 text-sm font-light text-[#007c7a]">
              Code {checkoutPowerCode} will be checked after you sign in.
            </p>
          )}
        </div>
      )}

      <div className="mt-5 rounded-2xl border border-[#cddbd6] bg-[#f4fbf8] p-4">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={keepSignedIn}
            onChange={(event) => updateKeepSignedIn(event.target.checked)}
            data-marginglow-ignore-work="true"
            className="mt-1 h-4 w-4 accent-[#007c7a]"
          />

          <span>
            <span className="block text-sm font-normal text-[#17322e]">
              Keep me signed in on this trusted device
            </span>
            <span className="mt-1 block text-xs font-light leading-5 text-[#52635f]">
              Untick this on a shared or public computer.
            </span>
          </span>
        </label>
      </div>

      <button
        onClick={googleLogin}
        disabled={busy}
        className={`w-full rounded-2xl border border-[#cddbd6] bg-white px-5 py-4 font-light text-[#092723] shadow-sm transition hover:bg-[#f4fbf8] disabled:opacity-60 ${
          checkoutIntent ? "mt-4" : "mt-6"
        }`}
      >
        {checkoutIntent
          ? checkoutPowerCode
            ? "Continue with Google and apply Power Code"
            : "Continue with Google to payment"
          : glowCheckReady
            ? "Continue with Google to see my result"
            : "Continue with Google"}
      </button>

      <div className="my-6 flex items-center gap-4 text-sm text-[#5f706b]">
        <div className="h-px flex-1 bg-[#dbe7e2]" />
        or
        <div className="h-px flex-1 bg-[#dbe7e2]" />
      </div>

      <form onSubmit={emailLogin} data-auth-form="true" className="space-y-4">
        {mode === "signup" && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-light text-[#17322e]">
                  First name
                </span>
                <input
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  required
                  autoComplete="given-name"
                  className="w-full rounded-2xl border border-[#cddbd6] bg-[#fbf8f3] px-4 py-3 outline-none transition focus:border-[#007c7a] focus:bg-white"
                  placeholder="Your first name"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-light text-[#17322e]">
                  Last name
                </span>
                <input
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  required
                  autoComplete="family-name"
                  className="w-full rounded-2xl border border-[#cddbd6] bg-[#fbf8f3] px-4 py-3 outline-none transition focus:border-[#007c7a] focus:bg-white"
                  placeholder="Your last name"
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-2 block text-sm font-light text-[#17322e]">
                Business name
              </span>
              <input
                value={businessName}
                onChange={(event) => setBusinessName(event.target.value)}
                className="w-full rounded-2xl border border-[#cddbd6] bg-[#fbf8f3] px-4 py-3 outline-none transition focus:border-[#007c7a] focus:bg-white"
                placeholder="Your business name"
              />
            </label>
          </>
        )}

        <label className="block">
          <span className="mb-2 block text-sm font-light text-[#17322e]">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            className="w-full rounded-2xl border border-[#cddbd6] bg-[#fbf8f3] px-4 py-3 outline-none transition focus:border-[#007c7a] focus:bg-white"
            placeholder="you@business.com"
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-light text-[#17322e]">
            Password
          </span>
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={6}
            className="w-full rounded-2xl border border-[#cddbd6] bg-[#fbf8f3] px-4 py-3 outline-none transition focus:border-[#007c7a] focus:bg-white"
            placeholder="At least 6 characters"
          />
        </label>

        <div className="-mt-2 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="text-sm font-light text-[#007c7a]"
          >
            {showPassword ? "Hide password" : "Show password"}
          </button>

          {mode === "signin" && (
            <button
              type="button"
              onClick={resetPassword}
              disabled={busy}
              className="text-sm font-light text-[#007c7a] transition hover:text-[#006b69] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Forgot password?
            </button>
          )}
        </div>

        {mode === "signup" && (
          <div className="rounded-2xl bg-[#f4fbf8] p-4 text-xs leading-5 text-[#52635f]">
            By continuing, you agree to our Terms and Privacy Policy. You can
            control data preferences inside your workspace.
          </div>
        )}

        {message && (
          <p className="rounded-2xl bg-[#fff1ec] p-3 text-sm text-[#bd4426]">
            {message}
          </p>
        )}

        <button
          disabled={busy}
          className="w-full rounded-2xl bg-[#007c7a] px-5 py-4 font-light text-white shadow-lg shadow-[#007c7a]/20 transition hover:bg-[#006b69] disabled:opacity-60"
        >
          {busy
            ? "Working..."
            : checkoutIntent
              ? checkoutPowerCode
                ? mode === "signin"
                  ? "Sign in and apply Power Code"
                  : "Create account and apply Power Code"
                : mode === "signin"
                  ? "Sign in and continue to payment"
                  : "Create account and continue to payment"
              : glowCheckReady
                ? mode === "signin"
                  ? "Sign in and see my complete result"
                  : "Create account and see my complete result"
                : mode === "signin"
                  ? "Let’s Make It Glow"
                  : "Create account and start my Free Business Check-up"}
        </button>
      </form>

      <button
        onClick={() => {
          setMessage("");
          setMode(mode === "signin" ? "signup" : "signin");
        }}
        className="mt-6 text-sm font-light text-[#007c7a]"
      >
        {mode === "signin"
          ? "New to MarginGlow? Create an account."
          : "Already have an account? Sign in."}
      </button>
    </div>
  );
}

function LandingPage() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signup");
  const [checkoutIntent, setCheckoutIntent] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [landingPowerCode, setLandingPowerCode] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [country, setCountry] = useState("");
  const [website, setWebsite] = useState("");
  const [googleBusinessProfile, setGoogleBusinessProfile] = useState("");
  const [addressOrArea, setAddressOrArea] = useState("");
  const [places, setPlaces] = useState<any[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [placesError, setPlacesError] = useState("");
  const [businessContext, setBusinessContext] = useState("");
  const [landingVoiceListening, setLandingVoiceListening] = useState(false);
  const [landingVoiceError, setLandingVoiceError] = useState("");
  const landingVoiceRecognitionRef = useRef<any>(null);
  const landingVoiceBaseRef = useRef("");
  const [showTesterWelcome, setShowTesterWelcome] = useState(false);
  const [glowResult, setGlowResult] = useState<any>(null);

  useEffect(() => {
    if (!glowResult) {
      return;
    }

    const scrollTimer = window.setTimeout(() => {
      document.getElementById("glow-results")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 350);

    return () => {
      window.clearTimeout(scrollTimer);
    };
  }, [glowResult]);

  useEffect(() => {
    if (!TESTER_MODE || typeof window === "undefined") {
      return;
    }

    const dismissed = window.localStorage.getItem(
      "marginglow_tester_welcome_dismissed",
    );

    if (!dismissed) {
      setShowTesterWelcome(true);
    }
  }, []);

  function closeTesterWelcome() {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        "marginglow_tester_welcome_dismissed",
        "true",
      );
    }

    setShowTesterWelcome(false);
  }
  const [glowLoading, setGlowLoading] = useState(false);
  const [glowStatus, setGlowStatus] = useState("");
  const [glowError, setGlowError] = useState("");

  function stopLandingVoiceInput() {
    try {
      landingVoiceRecognitionRef.current?.stop();
    } catch {
      // Recognition may already have stopped.
    }

    landingVoiceRecognitionRef.current = null;
    setLandingVoiceListening(false);
  }

  function startLandingVoiceInput() {
    setLandingVoiceError("");

    if (typeof window === "undefined") {
      setLandingVoiceError("Voice input is not available in this browser.");
      return;
    }

    const browserWindow = window as any;
    const SpeechRecognition =
      browserWindow.SpeechRecognition || browserWindow.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setLandingVoiceError(
        "Voice input is not supported by this browser. You can still type to Glow.",
      );
      return;
    }

    if (landingVoiceListening) {
      stopLandingVoiceInput();
      return;
    }

    landingVoiceBaseRef.current = businessContext.trim();

    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang =
      typeof navigator !== "undefined"
        ? navigator.language || "en-IE"
        : "en-IE";

    let confirmedTranscript = "";

    recognition.onstart = () => {
      setLandingVoiceListening(true);
      setLandingVoiceError("");
    };

    recognition.onresult = (event: any) => {
      let interimTranscript = "";

      for (
        let resultIndex = event.resultIndex;
        resultIndex < event.results.length;
        resultIndex += 1
      ) {
        const transcript = event.results[resultIndex]?.[0]?.transcript || "";

        if (event.results[resultIndex].isFinal) {
          confirmedTranscript += `${transcript} `;
        } else {
          interimTranscript += transcript;
        }
      }

      const spokenText = `${confirmedTranscript}${interimTranscript}`.trim();

      if (!spokenText) {
        return;
      }

      const existingText = landingVoiceBaseRef.current;
      const combinedText = [existingText, spokenText]
        .filter(Boolean)
        .join(existingText ? " " : "")
        .slice(0, 2000);

      setBusinessContext(combinedText);
      setGlowError("");
    };

    recognition.onerror = (event: any) => {
      const errorCode = String(event?.error || "");

      if (errorCode === "not-allowed" || errorCode === "service-not-allowed") {
        setLandingVoiceError(
          "Microphone access was blocked. Allow microphone access in your browser, then try again.",
        );
      } else if (errorCode !== "aborted") {
        setLandingVoiceError(
          "Glow could not hear clearly. Check your microphone and try again, or type instead.",
        );
      }

      setLandingVoiceListening(false);
    };

    recognition.onend = () => {
      landingVoiceRecognitionRef.current = null;
      setLandingVoiceListening(false);
    };

    landingVoiceRecognitionRef.current = recognition;

    try {
      recognition.start();
      trackEvent("landing_voice_input_started", {
        input_location: "free_business_checkup",
      });
    } catch {
      landingVoiceRecognitionRef.current = null;
      setLandingVoiceListening(false);
      setLandingVoiceError(
        "Voice input could not start. Please wait a moment and try again.",
      );
    }
  }

  useEffect(() => {
    return () => {
      try {
        landingVoiceRecognitionRef.current?.abort();
      } catch {
        // Ignore browser cleanup errors.
      }
    };
  }, []);

  async function findGoogleListing() {
    setPlacesLoading(true);
    setPlacesError("");
    setPlaces([]);
    setSelectedPlace(null);

    try {
      const response = await fetch("/api/find-google-listing", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessName,
          addressOrArea,
          country,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Glow could not find matching Google listings.",
        );
      }

      setPlaces(data.places || []);

      if (!data.places?.length) {
        setPlacesError(
          "No matching Google listings found. Try adding your street, area or postcode.",
        );
      }
    } catch (error) {
      setPlacesError(
        error instanceof Error
          ? error.message
          : "Glow could not find matching Google listings.",
      );
    } finally {
      setPlacesLoading(false);
    }
  }
  async function runFreeGlowCheck(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setGlowLoading(true);
    setGlowStatus("Glow is reading your business details...");
    window.setTimeout(() => {
      document
        .getElementById("free-glow-check")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    setGlowError("");
    setGlowResult(null);

    try {
      window.setTimeout(() => {
        setGlowStatus("Mapping how your business sells...");
      }, 700);

      window.setTimeout(() => {
        setGlowStatus(
          "Finding sales channels, customer paths and opportunities...",
        );
      }, 1700);

      window.setTimeout(() => {
        setGlowStatus("Building your Glow Business Map...");
      }, 3000);
      const response = await fetch("/api/free-glow-check", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessName,
          businessType,
          country,
          website,
          googleBusinessProfile,
          selectedGooglePlace: selectedPlace,
          addressOrArea,
          businessContext,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Glow could not complete the check.");
      }
      setGlowStatus(
        "Great success. Glow mapped your business and found opportunities.",
      );
      const savedGlowCheck = {
        result: data.result,
        businessName,
        businessType,
        country,
        website,
        addressOrArea,
        googleBusinessProfile,
        selectedPlace,
        businessContext,
        aiFootprintKgEstimate:
          typeof data.estimatedAiFootprintKg === "number"
            ? data.estimatedAiFootprintKg
            : 0.0001,
        aiTokenCount:
          typeof data.usage?.totalTokenCount === "number"
            ? data.usage.totalTokenCount
            : 0,
        createdAt: new Date().toISOString(),
      };

      window.localStorage.setItem(
        "marginglow_pending_glow_check",
        JSON.stringify(savedGlowCheck),
      );

      trackEvent("free_glow_check_completed", {
        business_type: businessType || "not_provided",
        country: country || "not_provided",
        google_listing_selected: Boolean(selectedPlace),
        website_provided: Boolean(website),
        business_map_ready: Boolean(data.result?.businessMap),
        business_map_channels: Array.isArray(
          data.result?.businessMap?.salesChannels,
        )
          ? data.result.businessMap.salesChannels.length
          : 0,
      });

      setGlowResult(data.result);
    } catch (error) {
      setGlowError(
        error instanceof Error
          ? error.message
          : "Glow could not complete the check. Please try again.",
      );
    } finally {
      setGlowLoading(false);
    }
  }

  function openAuth(
    mode: "signin" | "signup",
    shouldContinueToCheckout = false,
  ) {
    setAuthMode(mode);
    setCheckoutIntent(shouldContinueToCheckout);
    setAuthOpen(false);
    window.requestAnimationFrame(() => {
      document.getElementById("permanent-auth-panel")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });

    if (typeof window !== "undefined") {
      if (shouldContinueToCheckout) {
        window.localStorage.setItem("marginglow_auth_intent", "pro_scan");
      } else {
        window.localStorage.removeItem("marginglow_pending_checkout");
        window.localStorage.setItem("marginglow_auth_intent", "free_checkup");
      }
    }

    window.setTimeout(() => {
      document.getElementById("auth-panel")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }

  function beginFullScanCheckout() {
    window.localStorage.setItem(
      "marginglow_pending_checkout",
      JSON.stringify({
        plan: "instant_glow_scan",
        businessName: businessName.trim(),
        powerCode: landingPowerCode.trim().toUpperCase(),
        createdAt: new Date().toISOString(),
      }),
    );

    openAuth("signup", true);
  }

  return (
    <main className="min-h-screen bg-[#fbf8f3] text-[#092723]">
      {showTesterWelcome && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#092723]/70 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tester-welcome-title"
        >
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl md:p-8">
            <div className="rounded-3xl bg-[#092723] p-6 text-white">
              <p className="text-sm font-light uppercase tracking-[0.18em] text-[#61e6dc]">
                You are helping shape the future
              </p>

              <h2
                id="tester-welcome-title"
                className="mt-3 text-3xl font-light md:text-4xl"
              >
                Welcome to MarginGlow!
              </h2>

              <p className="mt-4 font-light leading-7 text-[#d7e5df]">
                Thanks for helping test the platform, it means so much.
              </p>

              <p className="mt-3 font-light leading-7 text-[#d7e5df]">
                Our mission is to deliver powerful, game-changing AI to every
                small business in the world.
              </p>

              <p className="mt-3 font-light leading-7 text-[#d7e5df]">
                You can use this demo to test MarginGlow on your own business, a
                friend&apos;s business, a business you know from your
                neighbourhood, or any business you can think of. The more real
                the business, the more useful the results will be.
              </p>
            </div>

            <div className="mt-6 grid gap-5 md:grid-cols-2">
              <div className="rounded-3xl bg-[#f4fbf8] p-5">
                <h3 className="text-xl font-light text-[#092723]">
                  Your MarginGlow testing journey
                </h3>

                <ol className="mt-4 space-y-3 text-sm font-light leading-6 text-[#52635f]">
                  <li>1. Get your Free Business Check-up.</li>
                  <li>2. Select the correct Google Business Profile.</li>
                  <li>3. Explore your personalised dashboard.</li>
                  <li>
                    4. Start your Pro Scan and let Glow's AI agents prepare the
                    first work.
                  </li>
                  <li>5. Add useful actions to your To-do list.</li>
                  <li>
                    6. Tell us what worked, what confused you and what would
                    make MarginGlow more useful.
                  </li>
                </ol>
              </div>

              <div className="rounded-3xl border border-[#ffb7a7] bg-[#fff5f1] p-5">
                <p className="text-sm font-light uppercase tracking-[0.16em] text-[#c63d27]">
                  Stripe test mode
                </p>

                <h3 className="mt-2 text-xl font-light text-[#092723]">
                  No real payment will be taken
                </h3>

                <p className="mt-3 text-sm font-light leading-6 text-[#52635f]">
                  When Stripe asks for payment details, use:
                </p>

                <div className="mt-4 space-y-3 rounded-2xl bg-white p-4 text-sm font-light leading-6 text-[#52635f]">
                  <p>
                    <span className="font-normal text-[#092723]">
                      Card number:
                    </span>{" "}
                    4242 4242 4242 4242
                  </p>

                  <p>
                    <span className="font-normal text-[#092723]">
                      Expiry date:
                    </span>{" "}
                    Any future date, for example 12/30
                  </p>

                  <p>
                    <span className="font-normal text-[#092723]">CVC:</span> Any
                    three digits, for example 123
                  </p>

                  <p>
                    <span className="font-normal text-[#092723]">
                      Postcode:
                    </span>{" "}
                    Any valid postcode, Eircode or ZIP code
                  </p>
                </div>

                <p className="mt-4 rounded-2xl bg-white p-3 text-sm font-normal leading-6 text-[#c63d27]">
                  MarginGlow is not currently taking real payments. These
                  details only simulate a successful Stripe payment.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-3xl border border-[#d7e5df] p-5">
              <h3 className="text-lg font-light text-[#092723]">
                Important testing information
              </h3>

              <p className="mt-3 text-sm font-light leading-6 text-[#52635f]">
                MarginGlow is in active public testing. Test accounts, uploaded
                information and testing data may be deleted at the end of the
                testing period.
              </p>

              <p className="mt-3 text-base font-normal leading-6 text-[#007c7a]">
                MarginGlow AI will be available for paid users in August 2026.
              </p>

              <p className="mt-3 text-sm font-light leading-6 text-[#52635f]">
                Your feedback will directly influence the product we launch.
              </p>
            </div>

            <button
              type="button"
              onClick={closeTesterWelcome}
              className="mt-6 w-full rounded-2xl bg-[#ff5a3d] px-6 py-4 text-base font-normal text-[#102521] transition hover:bg-[#ff765e]"
            >
              Start Exploring
            </button>
          </div>
        </div>
      )}

      <header className="relative mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-6">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <img
              src="/marginglow-logo.png"
              alt="MarginGlow"
              width="460"
              height="115"
              className="h-auto w-[190px] max-w-[52vw] sm:w-[230px]"
            />
            <p className="mt-1 hidden text-xs text-[#5f706c] sm:block">
              Your AI workforce for better margins.
            </p>
          </div>

          <div className="hidden items-center gap-4 md:flex">
            <a
              href="/small-business-101"
              onClick={() =>
                trackEvent("small_business_101_nav_click", {
                  link_location: "public_header",
                })
              }
              className="text-sm font-light text-[#092723] transition hover:text-[#007c7a]"
            >
              Small Business 101
            </a>

            <button
              onClick={() => openAuth("signin")}
              className="text-sm font-light text-[#092723]"
            >
              Sign in
            </button>

            <button
              onClick={() =>
                document
                  .getElementById("free-glow-check")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="rounded-2xl bg-[#007c7a] px-5 py-3 text-sm font-light text-white shadow-lg shadow-[#007c7a]/20 transition hover:bg-[#006b69]"
            >
              Start free Check-up
            </button>
          </div>

          <button
            type="button"
            onClick={() => setMobileMenuOpen((current) => !current)}
            aria-expanded={mobileMenuOpen}
            aria-label="Open navigation menu"
            className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#cddbd6] bg-white text-2xl font-light text-[#092723] shadow-sm md:hidden"
          >
            {mobileMenuOpen ? "×" : "☰"}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="absolute left-4 right-4 top-full z-50 mt-2 rounded-3xl border border-[#d7e5df] bg-white p-4 shadow-2xl shadow-[#092723]/15 md:hidden">
            <div className="grid gap-3">
              <a
                href="/small-business-101"
                onClick={() =>
                  trackEvent("small_business_101_nav_click", {
                    link_location: "public_mobile_menu",
                  })
                }
                className="rounded-2xl border border-[#cddbd6] bg-white px-5 py-4 text-left text-base font-light text-[#092723]"
              >
                Small Business 101
              </a>

              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  document
                    .getElementById("free-glow-check")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="rounded-2xl bg-[#007c7a] px-5 py-4 text-left text-base font-light text-white"
              >
                Start free Check-up
              </button>

              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  document
                    .getElementById("pricing-options")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="rounded-2xl bg-[#fff1ec] px-5 py-4 text-left text-base font-light text-[#c63d27]"
              >
                View pricing
              </button>

              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  openAuth("signin");
                }}
                className="rounded-2xl border border-[#cddbd6] bg-white px-5 py-4 text-left text-base font-light text-[#092723]"
              >
                Sign in
              </button>
            </div>
          </div>
        )}
      </header>

      <LandingWowExperience
        businessName={businessName}
        businessType={businessType}
        website={website}
        selectedPlace={selectedPlace}
        glowLoading={glowLoading}
        glowResult={glowResult}
        glowStatus={glowStatus}
        onStartFree={() =>
          document.getElementById("free-glow-check")?.scrollIntoView({
            behavior: "smooth",
          })
        }
        onStartPro={beginFullScanCheckout}
      />

      <section id="start" className="mx-auto max-w-7xl space-y-6 px-6 pb-10">
        {authOpen ? (
          <div id="auth-panel" className="scroll-mt-10">
            <AuthPanel
              initialMode={authMode}
              checkoutIntent={checkoutIntent}
              glowCheckReady={Boolean(glowResult)}
            />
          </div>
        ) : (
          <div
            id="free-glow-check"
            className="scroll-mt-10 overflow-hidden rounded-[2rem] border border-[#d7e5df] bg-white p-6 shadow-sm md:p-8"
          >
            <div className="border-b border-[#e2ece8] pb-4">
              <div>
                <p className="text-xs font-light uppercase tracking-[0.2em] text-[#007c7a]">
                  Free Business Check-up
                </p>

                <h2 className="mt-1 text-3xl font-light tracking-[-0.04em] text-[#092723] md:text-4xl">
                  Tell Glow about your business.
                </h2>
              </div>

              <button
                type="button"
                onClick={() =>
                  document.getElementById("business-name-input")?.focus()
                }
                className="group relative mt-4 w-full overflow-hidden rounded-3xl border border-[#174a43] bg-[#092723] p-4 text-left text-white shadow-[0_18px_45px_rgba(9,39,35,0.18)] transition hover:-translate-y-0.5 hover:border-[#61e6dc] md:p-5"
              >
                <span className="absolute inset-0 bg-[radial-gradient(circle_at_18%_50%,rgba(97,230,220,0.18),transparent_34%),radial-gradient(circle_at_82%_20%,rgba(255,90,61,0.10),transparent_32%)]" />

                <span className="relative flex flex-col gap-4 sm:flex-row sm:items-center">
                  <span className="relative flex h-20 w-20 shrink-0 items-center justify-center">
                    <span className="absolute inset-0 animate-ping rounded-full bg-[#61e6dc]/15 motion-reduce:animate-none" />
                    <span className="absolute inset-1 rounded-full bg-[#61e6dc]/15 blur-xl" />

                    <span className="relative animate-pulse motion-reduce:animate-none">
                      <GlowOrb
                        size="md"
                        state={
                          glowLoading
                            ? "working"
                            : glowResult
                              ? "complete"
                              : "ready"
                        }
                        spinning={glowLoading}
                        label={
                          glowLoading
                            ? "Glow is researching your business"
                            : glowResult
                              ? "Glow completed your Business Check-up"
                              : "Glow is ready"
                        }
                      />
                    </span>
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block text-xl font-light text-[#61e6dc] md:text-2xl">
                      {glowLoading
                        ? "Glow is researching your business"
                        : glowResult
                          ? "Your Business Map is ready"
                          : "Glow is ready. Start here."}
                    </span>

                    <span className="mt-2 block text-sm font-light leading-6 text-[#d7e5df]">
                      {glowLoading
                        ? "Watch Glow turn your business details into useful research."
                        : glowResult
                          ? "Open your results and review the first opportunities Glow found."
                          : "Enter a few business details below. About three minutes. No card required."}
                    </span>
                  </span>

                  <span className="shrink-0 text-3xl font-light text-[#61e6dc] transition group-hover:translate-y-1">
                    ↓
                  </span>
                </span>
              </button>
            </div>

            <form onSubmit={runFreeGlowCheck} className="mt-4 space-y-4">
              <div className="grid gap-4 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
                <div>
                  <label htmlFor="business-name-input" className="text-sm font-normal text-[#092723]">Business name</label>
                <input
                  id="business-name-input"
                  value={businessName}
                  onChange={(event) => setBusinessName(event.target.value)}
                  className="mt-2 h-[50px] w-full rounded-2xl border border-[#cddbd6] bg-[#fbf8f3] px-4 py-3 outline-none focus:border-[#007c7a]"
                  placeholder="Your business name"
                  autoComplete="organization"
                  required
                />
                </div>

                <div>
                  <label
                    htmlFor="business-description"
                    className="text-sm font-normal text-[#092723]"
                  >
                    Describe your business
                  </label>

                  <input
                    id="business-description"
                    value={businessType}
                    onChange={(event) => setBusinessType(event.target.value)}
                    className="mt-2 h-[50px] w-full rounded-2xl border border-[#cddbd6] bg-[#fbf8f3] px-4 py-3 text-[#092723] outline-none focus:border-[#007c7a]"
                    placeholder="For example: Independent hair salon specialising in colour and bridal styling."
                    maxLength={500}
                    required
                  />

                  <p className="mt-2 text-xs font-light leading-5 text-[#52635f]">
                    Describe your business in your own words. Examples:
                    neighbourhood cafe, independent hair salon, construction
                    contractor, SaaS company, dental clinic, online retailer or
                    professional services firm.
                  </p>

                  <p className="mt-2 text-xs font-normal leading-5 text-[#007c7a]">
                    Glow treats this as important owner-confirmed Business
                    Memory.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <input
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                  className="rounded-2xl border border-[#cddbd6] bg-[#fbf8f3] px-4 py-3 outline-none focus:border-[#007c7a]"
                  placeholder="Country"
                  required
                />

                <input
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  className="rounded-2xl border border-[#cddbd6] bg-[#fbf8f3] px-4 py-3 outline-none focus:border-[#007c7a]"
                  placeholder="Website"
                />
              </div>

              <div className="rounded-3xl border border-[#eaded7] bg-[#fffaf8] p-5">
                <div>
                  <label className="text-sm font-normal text-[#092723]">
                    Find your Google Business Profile
                  </label>

                  <p className="mt-2 text-sm leading-6 text-[#52635f]">
                    Not sure about your Google Business Profile? Enter your
                    address and we’ll see if we can find it.
                  </p>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                  <input
                    value={addressOrArea}
                    onChange={(event) => {
                      setAddressOrArea(event.target.value);
                      setSelectedPlace(null);
                    }}
                    className="w-full rounded-2xl border border-[#cddbd6] bg-white px-4 py-3 outline-none focus:border-[#007c7a]"
                    placeholder="Street address, area, Eircode, postcode or ZIP"
                  />

                  <button
                    type="button"
                    onClick={findGoogleListing}
                    disabled={
                      placesLoading ||
                      (!businessName.trim() && !addressOrArea.trim())
                    }
                    className="rounded-2xl bg-[#007c7a] px-6 py-3 text-sm font-normal text-white shadow-sm transition hover:bg-[#006b69] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {placesLoading ? "Searching..." : "Find my Google listing"}
                  </button>
                </div>

                <input
                  value={googleBusinessProfile}
                  onChange={(event) =>
                    setGoogleBusinessProfile(event.target.value)
                  }
                  className="mt-4 w-full rounded-2xl border border-[#cddbd6] bg-white px-4 py-3 outline-none focus:border-[#007c7a]"
                  placeholder="Already have a Google Maps link? Paste it here, optional."
                />

                {placesError && (
                  <p className="mt-4 rounded-2xl bg-[#fff1ec] p-4 text-sm text-[#bd4426]">
                    {placesError}
                  </p>
                )}

                {places.length > 0 && (
                  <div className="mt-5 space-y-3">
                    <div className="rounded-2xl border border-[#b9ddd6] bg-white p-4">
                      <p className="font-normal text-[#092723]">
                        Is one of these your business?
                      </p>

                      <p className="mt-1 text-sm leading-6 text-[#52635f]">
                        Confirm the correct profile so Glow uses the right
                        reviews, services, location and Google visibility
                        signals.
                      </p>
                    </div>

                    {places.map((place) => (
                      <button
                        type="button"
                        key={place.id}
                        onClick={() => setSelectedPlace(place)}
                        className={`w-full rounded-2xl border p-4 text-left transition ${
                          selectedPlace?.id === place.id
                            ? "scale-[1.01] border-[#007c7a] bg-white shadow-[0_0_0_4px_rgba(0,124,122,0.12),0_18px_45px_rgba(0,124,122,0.16)]"
                            : "border-[#d7e5df] bg-white/70 hover:border-[#007c7a] hover:bg-white hover:shadow-sm"
                        }`}
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <p className="font-normal text-[#092723]">
                              {place.name}
                            </p>

                            <p className="mt-1 text-sm text-[#52635f]">
                              {place.address}
                            </p>

                            <p className="mt-1 text-xs font-light uppercase tracking-[0.18em] text-[#007c7a]">
                              {place.category}
                            </p>
                          </div>

                          <div className="text-sm text-[#52635f] md:text-right">
                            {place.rating && (
                              <p>
                                {place.rating} stars
                                {place.userRatingCount
                                  ? ` · ${place.userRatingCount} reviews`
                                  : ""}
                              </p>
                            )}

                            <p
                              className={`mt-3 rounded-full px-4 py-2 text-center text-sm ${
                                selectedPlace?.id === place.id
                                  ? "bg-[#007c7a] text-white"
                                  : "border border-[#007c7a] text-[#007c7a]"
                              }`}
                            >
                              {selectedPlace?.id === place.id
                                ? "✓ Confirmed as my business"
                                : "Yes, this is my business"}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}

                    {selectedPlace && (
                      <div className="rounded-2xl border border-[#cbe4de] bg-[#eef8f5] p-4">
                        <p className="font-normal text-[#092723]">
                          Google profile confirmed.
                        </p>

                        <p className="mt-2 text-sm leading-6 text-[#52635f]">
                          Glow will use this profile for your Business Check-up.
                        </p>

                        <p className="mt-3 text-sm leading-6 text-[#52635f]">
                          Don’t control this profile yet? Glow will help you
                          understand Google’s current claim or access process so
                          you can manage the listing, reply to reviews and keep
                          the information accurate.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div className="overflow-hidden rounded-2xl border border-[#9dcec7] bg-[#e9f7f4] transition focus-within:border-[#007c7a] focus-within:ring-2 focus-within:ring-[#007c7a]/10">
                  <textarea
                    id="landing-business-context"
                    value={businessContext}
                    onChange={(event) => {
                      setBusinessContext(event.target.value);
                      setLandingVoiceError("");
                    }}
                    aria-describedby="landing-voice-status landing-voice-privacy"
                    className="min-h-32 w-full resize-y bg-transparent px-4 py-3 text-[#092723] outline-none placeholder:text-[#6f817d]"
                    placeholder="Talk or type to Glow... Example: We want more weekday bookings, better Google reviews and lower product waste."
                    maxLength={2000}
                  />

                  <div className="flex flex-col gap-3 border-t border-[#b9ddd6] bg-white/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={
                        landingVoiceListening
                          ? stopLandingVoiceInput
                          : startLandingVoiceInput
                      }
                      aria-pressed={landingVoiceListening}
                      aria-label={
                        landingVoiceListening
                          ? "Stop listening: stop voice input"
                          : "Talk to Glow: start voice input"
                      }
                      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-normal transition focus:outline-none focus:ring-2 focus:ring-[#007c7a]/30 ${
                        landingVoiceListening
                          ? "bg-[#ff5a3d] text-[#102521] hover:bg-[#ff765e]"
                          : "border border-[#007c7a] bg-white text-[#007c7a] hover:bg-[#f4fbf8]"
                      }`}
                    >
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        className="h-5 w-5"
                      >
                        <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z" />
                        <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3M9 21h6" />
                      </svg>
                      {landingVoiceListening
                        ? "Stop listening"
                        : "Talk to Glow"}
                    </button>

                    <p
                      id="landing-voice-status"
                      aria-live="polite"
                      className="text-xs font-light leading-5 text-[#52635f]"
                    >
                      {landingVoiceListening
                        ? "Glow is listening. Speak naturally and your words will appear above."
                        : "Use your microphone or keyboard. You can edit everything before submitting."}
                    </p>
                  </div>
                </div>

                <p
                  id="landing-voice-privacy"
                  className="mt-2 text-xs font-light leading-5 text-[#52635f]"
                >
                  Your browser handles transcription. MarginGlow receives the
                  text, not a voice recording, when you run the check-up.
                </p>

                {landingVoiceError && (
                  <p
                    role="alert"
                    className="mt-3 rounded-2xl border border-[#f0b8a8] bg-[#fff5f1] px-4 py-3 text-sm font-light leading-6 text-[#bd4426]"
                  >
                    {landingVoiceError}
                  </p>
                )}
              </div>

              <button
                disabled={glowLoading}
                className="w-full rounded-2xl bg-[#007c7a] px-6 py-4 font-light text-white shadow-lg shadow-[#007c7a]/20 disabled:opacity-60"
              >
                {glowLoading
                  ? "Glow is mapping my business..."
                  : "Run my Free Business Check-up"}
              </button>
            </form>
          </div>
        )}

        <div
          id="glow-results"
          className="marginglow-glow-response-surface w-full scroll-mt-10 rounded-[2rem] border border-[#efcfc3] bg-[#fff6ef] p-5 shadow-sm md:p-8"
        >
          {!glowLoading && !glowResult && !glowError && (
            <div>
              <p className="text-sm font-light uppercase tracking-[0.18em] text-[#b64229]">
                Glow response: Free Business Check-up
              </p>
              <h3 className="mt-3 text-3xl font-light text-[#092723]">
                Your result will appear here.
              </h3>
              <p className="mt-4 text-base leading-7 text-[#52635f]">
                Fill in the form, find your Google listing and tell Glow what
                you want to improve. Your free business health check will appear
                here beside the form.
              </p>

              <div className="mt-6 rounded-3xl border border-[#efcfc3] bg-white p-5 text-sm leading-6 text-[#52635f]">
                <p className="font-light text-[#092723]">Glow will look for</p>
                <p className="mt-3">More revenue.</p>
                <p className="mt-2">Lower costs and waste.</p>
                <p className="mt-2">Better Google visibility.</p>
              </div>
            </div>
          )}

          {glowLoading && (
            <GlowResearchProgress
              businessName={businessName}
              businessType={businessType}
              website={website}
              addressOrArea={addressOrArea}
              googleBusinessProfile={googleBusinessProfile}
              selectedPlace={selectedPlace}
              businessContext={businessContext}
              status={glowStatus}
            />
          )}

          {glowError && (
            <p className="rounded-2xl bg-[#fff1ec] p-4 text-sm text-[#bd4426]">
              {glowError}
            </p>
          )}

          {glowResult && (
            <div>
              <div className="rounded-3xl bg-[#092723] p-5 text-white shadow-[0_18px_45px_rgba(9,39,35,0.18)]">
                <p className="text-sm font-light uppercase tracking-[0.22em] text-[#61e6dc]">
                  Your first Glow win
                </p>

                <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <GlowOrb
                    state="complete"
                    size="sm"
                    label="Glow completed your first Business Map"
                  />

                  <div>
                    <p className="text-2xl font-light">
                      Your Glow Business Map is ready.
                    </p>

                    <p className="mt-2 text-sm font-light leading-6 text-[#d7e5df]">
                      Glow has mapped how your business appears to sell, serve
                      customers and create value, then prepared a specific early
                      opportunity for you to review.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="mt-6 text-2xl font-light">
                {glowResult.headline}
              </h3>
              <p className="mt-3 text-sm leading-6 text-[#52635f]">
                {glowResult.summary}
              </p>

              <div className="mt-5 grid gap-4 md:grid-cols-3">
                <div>
                  <h4 className="font-light">Revenue</h4>
                  <ul className="mt-3 space-y-2 text-sm text-[#52635f]">
                    {glowResult.revenueOpportunities
                      ?.slice(0, 1)
                      .map((item: any) => (
                        <li key={item.title}>• {item.title}</li>
                      ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-light">Cost and waste</h4>
                  <ul className="mt-3 space-y-2 text-sm text-[#52635f]">
                    {glowResult.costWasteOpportunities
                      ?.slice(0, 1)
                      .map((item: any) => (
                        <li key={item.title}>• {item.title}</li>
                      ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-light">Google growth</h4>
                  <ul className="mt-3 space-y-2 text-sm text-[#52635f]">
                    {glowResult.googleGrowthActions
                      ?.slice(0, 1)
                      .map((item: any) => (
                        <li key={item.title}>• {item.title}</li>
                      ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 rounded-3xl border border-[#efcfc3] bg-white p-5">
                <p className="text-sm font-light uppercase tracking-[0.18em] text-[#007c7a]">
                  More opportunities are ready
                </p>

                <h4 className="mt-2 text-xl font-light text-[#092723]">
                  Your complete Check-up includes more than this preview.
                </h4>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {[
                    "More revenue opportunities.",
                    "Your complete Google Growth Stack.",
                    "Your prioritised next best actions.",
                    "Your saved business context and Google listing.",
                  ].map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl border border-[#d7e5df] bg-white p-4 text-sm font-light leading-6 text-[#52635f]"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {glowResult?.businessMap && (
                <GlowBusinessMap businessMap={glowResult.businessMap} preview />
              )}

              <div className="mt-6 rounded-3xl bg-[#fbf8f3] p-5">
                <p className="text-sm font-light uppercase tracking-[0.2em] text-[#007c7a]">
                  Your complete result is ready
                </p>

                <h4 className="mt-2 text-2xl font-light text-[#092723]">
                  Glow has found more opportunities for your business.
                </h4>

                <p className="mt-3 text-sm leading-6 text-[#52635f]">
                  Create your free account to see the complete Free Business
                  Check-up, save every result and open your private MarginGlow
                  dashboard.
                </p>

                <button
                  type="button"
                  onClick={() => openAuth("signup", false)}
                  className="mt-5 w-full rounded-2xl bg-[#007c7a] px-6 py-4 text-base font-normal text-white shadow-lg shadow-[#007c7a]/20 transition hover:bg-[#006b69]"
                >
                  Create my free account and see the complete result
                </button>

                <p className="mt-3 text-center text-xs font-light leading-5 text-[#52635f]">
                  No card required. You will not need to enter your business
                  information again.
                </p>

                <div className="my-6 flex items-center gap-4 text-sm text-[#5f706b]">
                  <div className="h-px flex-1 bg-[#dbe7e2]" />
                  or put Glow to work now
                  <div className="h-px flex-1 bg-[#dbe7e2]" />
                </div>

                <div className="rounded-3xl border border-[#ff5a3d] bg-white p-5 shadow-[0_18px_45px_rgba(255,90,61,0.10)]">
                  <p className="text-sm font-light uppercase tracking-[0.18em] text-[#c63d27]">
                    Pro Scan included · €7 / US$7 monthly
                  </p>

                  <h5 className="mt-2 text-xl font-light text-[#092723]">
                    Glow's AI agents will work on the opportunities it found.
                  </h5>

                  <p className="mt-3 text-sm font-light leading-6 text-[#52635f]">
                    The Pro Scan researches your business, prioritises the
                    highest-value opportunities, prepares practical outputs and
                    builds your first 30-day Action Plan. Your first month of
                    Glow Pro is included.
                  </p>

                  <div className="mt-5 rounded-2xl bg-[#fff5f1] p-4">
                    <label
                      htmlFor="landing-power-code"
                      className="text-sm font-light text-[#092723]"
                    >
                      Have a Power Code or judge access code?
                    </label>

                    <input
                      id="landing-power-code"
                      type="text"
                      value={landingPowerCode}
                      onChange={(event) =>
                        setLandingPowerCode(
                          event.target.value
                            .replace(/[^a-z]/gi, "")
                            .toUpperCase(),
                        )
                      }
                      placeholder="Enter Power Code"
                      autoComplete="off"
                      spellCheck={false}
                      className="mt-2 w-full rounded-2xl border border-[#d7e5df] bg-white px-4 py-3 text-sm font-light uppercase tracking-[0.08em] text-[#092723] outline-none transition focus:border-[#007c7a]"
                    />

                    <p className="mt-2 text-xs font-light leading-5 text-[#52635f]">
                      Letters only. Capitalisation does not matter. Enter your
                      code before continuing.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={beginFullScanCheckout}
                    className="mt-4 w-full rounded-2xl bg-[#ff5a3d] px-6 py-4 text-base font-normal text-[#102521] shadow-lg shadow-[#ff5a3d]/20 transition hover:bg-[#ff765e]"
                  >
                    {landingPowerCode
                      ? "Continue and apply Power Code"
                      : "Start Pro · €7 / US$7 monthly"}
                  </button>

                  <p className="mt-3 text-center text-xs font-light leading-5 text-[#52635f]">
                    €7 or US$7 per month + VAT where applicable, starting today.
                    No separate activation fee. Renews monthly until cancelled.
                    A standard Power Code gives 20% off your first month:
                    €5.60 or US$5.60, then €7 or US$7/month, plus applicable VAT.
                    Selected invitation Power Codes provide complimentary
                    activation and 30 days of Glow Pro.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-10">
        <div className="overflow-hidden rounded-[2rem] border border-[#d7e5df] bg-white p-8 shadow-sm">
          <p className="text-sm font-light uppercase tracking-[0.22em] text-[#006b69]">
            Built for real businesses
          </p>

          <h2 className="mt-4 max-w-4xl text-4xl font-light tracking-tight text-[#092723]">
            Glow learns your kind of business. Then it learns your business.
          </h2>

          <p className="mt-5 max-w-3xl text-lg font-light leading-8 text-[#52635f]">
            MarginGlow begins with the sectors where busy owners face the same
            daily challenge: finding growth, protecting margin and reducing
            costly waste without adding another full-time job.
          </p>

          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[
              [
                "Hair salons",
                "Bookings",
                "/business-sectors-speed-v1/hair-salon.webp",
                "European stylist creating glamorous movie-star hair while photographers wait outside.",
                "Fill quieter appointment times, improve local discovery and bring clients back more often.",
              ],
              [
                "Beauty salons",
                "Retention",
                "/business-sectors-speed-v1/beauty-salon.webp",
                "Busy European beauty salon owner delivering a treatment in an active salon.",
                "Strengthen repeat bookings, reviews, packages and profitable treatment demand.",
              ],
              [
                "Restaurants and cafés",
                "Visits",
                "/business-sectors-speed-v1/restaurant-cafe.webp",
                "European café owner working quickly behind a busy counter during service.",
                "Find missed local demand, reduce waste and improve repeat visits.",
              ],
              [
                "Trades and home services",
                "Enquiries",
                "/business-sectors-speed-v1/trades-home-services.webp",
                "European tradesperson at an active renovation job with a small dog beside the tools.",
                "Capture enquiries faster, improve Google visibility and reduce lost admin time.",
              ],
              [
                "Consultants and professional services",
                "Expertise",
                "/business-sectors-speed-v1/professional-services.webp",
                "European consultant at a lived-in desk with papers, laptop, coffee and an enormous cat.",
                "Turn expertise into stronger offers, better follow-up and less repetitive work.",
              ],
              [
                "Shops and e-commerce",
                "Conversion",
                "/business-sectors-speed-v1/shop-ecommerce.webp",
                "European online shop owner packing customer orders in a busy stockroom.",
                "Spot conversion gaps, underused products and avoidable marketing waste.",
              ],
            ].map(([title, impact, image, alt, copy]) => (
              <article
                key={title}
                className="group overflow-hidden rounded-3xl border border-[#d7e5df] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#e9f3ef]">
                  <img
                    src={image}
                    srcSet={`${image.replace(".webp", "-480.webp")} 480w, ${image} 960w`}
                    sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                    alt={alt}
                    width="960"
                    height="720"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.04]"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#092723]/80 via-[#092723]/10 to-transparent" />

                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <div className="flex items-end justify-between gap-4">
                      <h3 className="max-w-[75%] text-2xl font-light leading-tight text-white">
                        {title}
                      </h3>

                      <span className="shrink-0 rounded-full bg-[#ff5a3d] px-3 py-1 text-[11px] font-normal uppercase tracking-[0.13em] text-[#102521] shadow-lg">
                        {impact}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <p className="text-sm font-light leading-6 text-[#52635f]">
                    {copy}
                  </p>
                </div>
              </article>
            ))}
          </div>

          <p className="mt-6 text-sm font-light leading-6 text-[#5f706b]">
            Every business is different. Describe yours in your own words during
            the Check-up, and Glow will use that owner-confirmed context before
            recommending anything.
          </p>
        </div>
      </section>

      <section
        id="one-glow-five-roles"
        className="mx-auto max-w-7xl scroll-mt-10 px-6 pb-10"
      >
        <div className="overflow-hidden rounded-[2rem] bg-[#092723] p-8 text-white shadow-[0_24px_70px_rgba(9,39,35,0.22)]">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-center">
            <div>
              <p className="text-sm font-light uppercase tracking-[0.22em] text-[#61e6dc]">
                Your Business A-Team
              </p>

              <h2 className="mt-4 max-w-4xl text-4xl font-light tracking-tight md:text-5xl">
                Meet your new senior team.
              </h2>

              <p className="mt-5 max-w-3xl text-lg font-light leading-8 text-[#d7e5df]">
                Five senior roles share one Business Map, work from the same
                priorities and prepare actions for your approval.
              </p>
            </div>

            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.05] p-5">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_45%,rgba(97,230,220,0.18),transparent_42%),radial-gradient(circle_at_80%_20%,rgba(255,90,61,0.10),transparent_34%)]" />

              <div className="relative flex items-center gap-5">
                <div className="relative flex h-24 w-24 shrink-0 items-center justify-center">
                  <span className="absolute inset-0 animate-ping rounded-full bg-[#61e6dc]/10 motion-reduce:animate-none" />
                  <span className="absolute inset-2 rounded-full bg-[#61e6dc]/15 blur-xl" />

                  <span className="relative animate-pulse motion-reduce:animate-none">
                    <GlowOrb
                      size="md"
                      state="working"
                      spinning
                      label="Glow is coordinating your Business A-Team"
                    />
                  </span>
                </div>

                <div>
                  <p className="text-lg font-light text-[#61e6dc]">
                    Glow is coordinating the team.
                  </p>

                  <p className="mt-2 text-sm font-light leading-6 text-[#d7e5df]">
                    One briefing. Shared priorities. Useful work prepared for
                    your approval.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              [
                "Nightshift Operations Manager",
                "Momentum",
                "Keeps research, preparation and useful work moving while you are busy running the business.",
              ],
              [
                "Marketing Director",
                "Demand",
                "Leads positioning, campaigns, content, customer retention and the specialist SEO work beneath them.",
              ],
              [
                "Commercial Director",
                "Margin",
                "Finds missed revenue, weak offers, unnecessary costs, margin leaks and operational friction.",
              ],
              [
                "Google Growth Director",
                "Visibility",
                "Leads your Google Business Profile, reviews, local discovery, Ads and future Workspace workflows.",
              ],
              [
                "Sustainability Director",
                "Efficiency",
                "Finds energy, resource, supplier and AI waste that costs money, then prepares credible sustainability evidence.",
              ],
            ].map(([title, impact, copy], index) => (
              <article
                key={title}
                className={`rounded-3xl border border-white/10 p-5 ${
                  index === 4
                    ? "bg-[#123b35] md:col-span-2 lg:col-span-1"
                    : "bg-white/[0.05]"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <p className="text-xs font-light uppercase tracking-[0.18em] text-[#61e6dc]">
                    {String(index + 1).padStart(2, "0")}
                  </p>

                  <span className="rounded-full bg-[#ff5a3d] px-3 py-1 text-xs font-normal uppercase tracking-[0.14em] text-[#102521] shadow-lg shadow-[#ff5a3d]/15">
                    {impact}
                  </span>
                </div>

                <h3 className="mt-4 text-xl font-light text-white">{title}</h3>

                <p className="mt-3 text-sm font-light leading-6 text-[#d7e5df]">
                  {copy}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-7 overflow-hidden rounded-3xl border border-white/10 bg-[#071d1a] py-4">
            <div className="flex w-max animate-[glowActionRail_32s_linear_infinite] gap-3 px-3 motion-reduce:animate-none">
              {[
                "Find missed demand",
                "Improve local discovery",
                "Strengthen reviews",
                "Spot weak offers",
                "Tighten pricing",
                "Reduce wasted spend",
                "Prepare useful work",
                "Cut admin waste",
                "Find margin leaks",
                "Improve customer follow-up",
                "Reduce energy waste",
                "Prepare tender answers",
                "Find missed demand",
                "Improve local discovery",
                "Strengthen reviews",
                "Spot weak offers",
                "Tighten pricing",
                "Reduce wasted spend",
                "Prepare useful work",
                "Cut admin waste",
                "Find margin leaks",
                "Improve customer follow-up",
                "Reduce energy waste",
                "Prepare tender answers",
              ].map((action, index) => (
                <span
                  key={`${action}-${index}`}
                  className="whitespace-nowrap rounded-full border border-[#61e6dc]/20 bg-[#123b35] px-4 py-2 text-sm font-light text-[#d7e5df]"
                >
                  <span className="mr-2 text-[#61e6dc]">✦</span>
                  {action}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-5 rounded-3xl border border-[#ffb55f]/20 bg-[#302019]/60 p-5">
            <p className="text-sm font-normal text-[#ffb55f]">
              One shared Business Map. No conflicting AI personalities.
            </p>

            <p className="mt-2 text-sm font-light leading-6 text-[#f3ddd3]">
              Glow coordinates the team, researches first and asks before any
              material action. You remain in control.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-10">
        <div className="rounded-[2rem] border border-[#d7e5df] bg-white p-8 shadow-sm">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-sm font-light uppercase tracking-[0.22em] text-[#006b69]">
                Built for busy owners
              </p>

              <h2 className="mt-4 text-4xl font-light tracking-tight text-[#092723]">
                You do not need to learn AI. You just need to run your business.
              </h2>

              <p className="mt-5 text-lg font-light leading-8 text-[#52635f]">
                MarginGlow turns everyday business data into clear actions: more
                revenue, lower costs, less waste, better Google visibility and
                tender-ready sustainability proof.
              </p>

              <button
                type="button"
                onClick={() =>
                  document
                    .getElementById("free-glow-check")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="mt-7 rounded-2xl bg-[#007c7a] px-7 py-4 text-base font-normal text-white shadow-xl shadow-[#007c7a]/20 transition hover:bg-[#006b69]"
              >
                Ask Glow about my business
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {[
                [
                  "Ask Glow Anything",
                  "Type or talk to Glow from your dashboard. Ask about sales, reviews, costs, waste, tenders or sustainability.",
                ],
                [
                  "Upload what you already have",
                  "Add invoices, receipts, bills, statements, screenshots, Google Ads exports or tender questions.",
                ],
                [
                  "Find answers again",
                  "Search previous Glow chats, uploads, reports, recommendations and tasks instead of asking the same question twice.",
                ],
                [
                  "Waste fewer Task Credits",
                  "Better memory and search means fewer repeated AI runs, lower costs and fewer unnecessary AI emissions.",
                ],
                [
                  "Turn chat into action",
                  "Glow can draft replies, create tasks, organise next steps and build customer-ready reports.",
                ],
                [
                  "Built for real business areas",
                  "Revenue, Google profile, reviews, costs, waste, sustainability, reports, tenders and documents.",
                ],
              ].map(([title, copy]) => (
                <div key={title} className="rounded-3xl bg-[#f4fbf8] p-5">
                  <h3 className="text-xl font-light text-[#092723]">{title}</h3>
                  <p className="mt-3 text-sm font-light leading-6 text-[#52635f]">
                    {copy}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 pb-10">
        <div className="rounded-[2rem] border border-[#d7e5df] bg-white p-8 shadow-sm">
          <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <p className="text-sm font-light uppercase tracking-[0.22em] text-[#006b69]">
                Google-powered growth
              </p>

              <h2 className="mt-4 text-4xl font-light tracking-tight text-[#092723]">
                Built to help small businesses get more value from Google.
              </h2>

              <p className="mt-5 text-lg font-light leading-8 text-[#52635f]">
                MarginGlow starts with your Google Business Profile and grows
                into a practical Google-powered operating desk for reviews, ads,
                inbox intelligence and customer-ready proof.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {[
                [
                  "Google Business Profile",
                  "Find review gaps, profile freshness issues, missing services and local visibility opportunities.",
                ],
                [
                  "Google Ads made simpler",
                  "Glow will help owners understand spend, spot waste and turn campaign confusion into clear actions.",
                ],
                [
                  "Google Workspace ready",
                  "We will help businesses move towards Google Workspace so Gmail, Drive and documents can power their dashboard.",
                ],
                [
                  "Built with Gemini on Google Cloud",
                  "The product story is Google-first: Gemini intelligence, Google Cloud infrastructure and Google business data.",
                ],
              ].map(([title, copy]) => (
                <div key={title} className="rounded-3xl bg-[#f4fbf8] p-5">
                  <h3 className="text-xl font-light text-[#092723]">{title}</h3>
                  <p className="mt-3 text-sm font-light leading-6 text-[#52635f]">
                    {copy}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section
        id="pricing-options"
        className="mx-auto grid max-w-7xl gap-6 px-6 pb-10 md:grid-cols-3"
      >
        {[
          [
            "1",
            "Free Business Check-up",
            "A free teaser using your Google listing, website and business context to find your first margin opportunities.",
            "Free · No card required",
          ],
          [
            "2",
            "Pro Scan",
            "A deeper AI review of your Google presence, website, costs, waste and growth opportunities. Includes your first month of MarginGlow Pro.",
            "€7 / US$7 per month · Scan included",
          ],
          [
            "3",
            "MarginGlow Pro",
            "Ongoing weekly actions, Ask Glow, searchable memory, dashboard access and 100 Task Credits per month.",
            "$7/month · Cancel anytime",
          ],
        ].map(([step, title, copy, detail]) => (
          <div
            key={title}
            className="rounded-[2rem] border border-[#d7e5df] bg-white p-8 shadow-sm"
          >
            <div className="mb-10 flex h-12 w-12 items-center justify-center rounded-full bg-[#e4f4ee] text-lg font-light text-[#007c7a]">
              {step}
            </div>
            <h3 className="text-2xl font-light tracking-tight">{title}</h3>
            <p className="mt-4 min-h-16 text-sm leading-6 text-[#52635f]">
              {copy}
            </p>
            <p className="mt-6 text-sm font-light text-[#007c7a]">{detail}</p>
          </div>
        ))}
      </section>

      <section
        id="permanent-auth-panel"
        className="mx-auto max-w-3xl scroll-mt-24 px-6 pb-12 pt-6"
      >
        <div className="mb-5 text-center">
          <p className="text-sm font-light uppercase tracking-[0.18em] text-[#007c7a]">
            Your private workspace
          </p>
          <h2 className="mt-3 text-3xl font-light text-[#092723]">
            Sign in to MarginGlow
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm font-light leading-7 text-[#52635f]">
            Return to your saved Check-ups, Business Map, Glow work, Task
            Credits and account settings.
          </p>
        </div>

        <AuthPanel
          initialMode={authMode}
          checkoutIntent={checkoutIntent}
          glowCheckReady={Boolean(glowResult)}
        />
      </section>

      <section
        id="meet-the-founders"
        className="mx-auto max-w-7xl px-6 pb-8 sm:pb-12"
      >
        <div className="overflow-hidden rounded-[2rem] border border-[#174a43] bg-[#092723] text-white shadow-[0_20px_55px_rgba(9,39,35,0.18)]">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
            <div className="relative min-h-[320px] overflow-hidden sm:min-h-[440px] lg:min-h-full">
              <img
                src="/founders-felix-roick-gary-byrnes.jpg"
                alt="Felix Roick on the left and Gary Byrnes on the right, founders of Tao Climate"
                width="1125"
                height="807"
                className="absolute inset-0 h-full w-full object-cover"
                style={{ objectPosition: "35% center" }}
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="p-7 sm:p-10 lg:p-12">
              <h2 className="text-4xl font-light tracking-tight sm:text-5xl">
                Meet the Founders.
              </h2>

              <p className="mt-3 text-xl font-light text-[#61e6dc] sm:text-2xl">
                We know your pain!
              </p>

              <div className="mt-7 space-y-5 text-base font-light leading-8 text-[#d7e5df]">
                <p>
                  Felix Roick (L) and Gary Byrnes (R) founded Tao Climate in
                  2022 with one simple mission: to stop climate change by
                  enabling the removal of CO2 from the atmosphere at scale, then
                  putting the removed carbon to good use. In Ukraine, Tao
                  Climate worked with local partners who grew industrial hemp to
                  remove CO2 from the atmosphere. The captured plant carbon was
                  used to build sustainable hempcrete housing for people
                  displaced by war, locking the carbon away for centuries. The
                  project earned Tao Climate a place in the XPRIZE Carbon
                  Removal Global Top 100.
                </p>

                <p>
                  MarginGlow is the newest element of Tao Climate OS, the
                  company’s range of innovative products designed to measure,
                  reduce and remove carbon emissions. Felix and Gary have worked
                  across companies large and small, in aviation, software,
                  e-commerce, operations and agricultural infrastructure. That
                  experience shaped MarginGlow AI’s goal: to make life easier
                  for all business owners. MarginGlow is designed to solve
                  everyday problems and grow every kind of business. As a bonus,
                  it gives businesses the opportunity to reduce the climate
                  impact of AI by using its capabilities more efficiently.
                </p>

                <p>
                  Tao Climate is proud to be supported by the European
                  Commission, Google Startups for Sustainable Development,
                  Enterprise Ireland and Google Cloud Startups. MarginGlow AI is
                  available to try now. The team can also be found on social
                  media, with more information available at www.TaoClimate.com.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="for-ai-agents"
        className="mx-auto max-w-7xl px-6 pb-8 sm:pb-12"
      >
        <div className="overflow-hidden rounded-[2rem] border border-[#9bcfc6] bg-[#e4f4ee] p-7 shadow-sm sm:p-10 lg:p-12">
          <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <p className="text-sm font-light uppercase tracking-[0.22em] text-[#006b69]">
                For AI agents
              </p>

              <h2 className="mt-4 max-w-3xl text-4xl font-light tracking-tight text-[#092723] sm:text-5xl">
                Give your agent a specialist small-business intelligence layer.
              </h2>

              <p className="mt-6 max-w-3xl text-lg font-light leading-8 text-[#52635f]">
                MarginGlow AI Signal gives authorised agents public evidence,
                three ranked commercial opportunities, confidence, likely impact
                and practical first actions for a specific small business. Your
                agent keeps its customer relationship. MarginGlow supplies the
                specialist intelligence.
              </p>

              <a
                href="/signal"
                onClick={() =>
                  trackEvent("signal_landing_click", {
                    product: "marginglow_ai_signal",
                    audience: "human",
                    link_location: "landing_agent_section",
                  })
                }
                className="mt-8 inline-flex rounded-full bg-[#092723] px-7 py-4 font-light text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#174a43]"
              >
                Explore MarginGlow AI Signal
              </a>
            </div>

            <div className="rounded-[2rem] border border-[#9bcfc6] bg-white p-7">
              <p className="text-sm font-light uppercase tracking-[0.18em] text-[#007c7a]">
                Live agent interface
              </p>

              <div className="mt-6 space-y-5">
                <div>
                  <p className="text-sm font-medium text-[#092723]">Protocol</p>
                  <p className="mt-1 text-sm font-light text-[#52635f]">
                    Model Context Protocol.
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-[#092723]">
                    Intelligence
                  </p>
                  <p className="mt-1 text-sm font-light leading-6 text-[#52635f]">
                    Evidence-backed revenue, margin, cost, conversion, Google
                    visibility and operational opportunities.
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-[#092723]">
                    Current access
                  </p>
                  <p className="mt-1 text-sm font-light leading-6 text-[#52635f]">
                    Authenticated beta access with one complimentary Signal.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function forceTopScroll() {
  if (typeof window === "undefined") {
    return;
  }

  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "instant" as ScrollBehavior,
  });
}

export default function Home() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutStarting, setCheckoutStarting] = useState(false);

  useEffect(() => {
    const fallbackTimer = window.setTimeout(() => {
      setLoading(false);
    }, 2000);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser) {
          setUser(null);
          setCheckoutStarting(false);
          return;
        }

        const pendingCheckoutRaw =
          typeof window !== "undefined"
            ? window.localStorage.getItem("marginglow_pending_checkout")
            : null;

        const authIntent =
          typeof window !== "undefined"
            ? window.localStorage.getItem("marginglow_auth_intent")
            : null;

        const shouldStartCheckout =
          authIntent === "pro_scan" && Boolean(pendingCheckoutRaw);

        if (!shouldStartCheckout && pendingCheckoutRaw) {
          window.localStorage.removeItem("marginglow_pending_checkout");
        }

        if (shouldStartCheckout) {
          setCheckoutStarting(true);
        }

        await saveUser(currentUser);

        if (
          currentUser.email?.trim().toLowerCase() === "hello@taoclimate.com"
        ) {
          try {
            const idToken = await currentUser.getIdToken();

            const internalAccessResponse = await fetch("/api/internal-access", {
              method: "POST",
              headers: {
                Authorization: `Bearer ${idToken}`,
              },
            });

            const internalAccessData = await internalAccessResponse.json();

            if (!internalAccessResponse.ok) {
              throw new Error(
                internalAccessData?.error ||
                  internalAccessData?.reason ||
                  "Internal access could not be confirmed.",
              );
            }

            console.info("MarginGlow internal access confirmed.");
          } catch (internalAccessError) {
            console.error(
              "Internal access could not be confirmed:",
              internalAccessError,
            );
          }
        }

        // Save the complete Free Business Check-up before opening the
        // dashboard or continuing to Stripe.
        await transferPendingGlowCheck(currentUser);

        window.localStorage.removeItem("marginglow_auth_intent");

        if (shouldStartCheckout && pendingCheckoutRaw) {
          try {
            const pendingCheckout = JSON.parse(pendingCheckoutRaw);

            if (pendingCheckout?.plan === "instant_glow_scan") {
              const pendingCode =
                typeof pendingCheckout.powerCode === "string"
                  ? pendingCheckout.powerCode
                      .trim()
                      .toUpperCase()
                      .replace(/[^A-Z]/g, "")
                  : "";

              if (pendingCode === "JUDGEGLOW") {
                const idToken = await currentUser.getIdToken();

                const judgeAccessResponse = await fetch("/api/judge-access", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${idToken}`,
                  },
                  body: JSON.stringify({
                    code: pendingCode,
                    businessName:
                      typeof pendingCheckout.businessName === "string"
                        ? pendingCheckout.businessName
                        : "",
                  }),
                });

                const judgeAccessData = await judgeAccessResponse.json();

                if (!judgeAccessResponse.ok) {
                  throw new Error(
                    judgeAccessData?.error ||
                      "Judge access could not be activated.",
                  );
                }

                window.localStorage.removeItem("marginglow_pending_checkout");

                setCheckoutStarting(false);
                setUser(currentUser);
                return;
              }

              const checkoutStarted = await startCheckout(
                "instant_glow_scan",
                currentUser.uid,
                typeof pendingCheckout.businessName === "string"
                  ? pendingCheckout.businessName
                  : "",
                pendingCode,
              );

              window.localStorage.removeItem("marginglow_pending_checkout");

              if (checkoutStarted) {
                return;
              }
            } else {
              window.localStorage.removeItem("marginglow_pending_checkout");
            }
          } catch (checkoutError) {
            console.error(
              "Pending checkout could not be restored:",
              checkoutError,
            );

            window.localStorage.removeItem("marginglow_pending_checkout");
          }

          setCheckoutStarting(false);
        }

        // The user is only shown the dashboard after the Check-up has
        // been transferred into their Firestore account.
        setUser(currentUser);
      } catch (error) {
        console.error("Auth state error:", error);
        setUser(currentUser);
        setCheckoutStarting(false);
      } finally {
        window.clearTimeout(fallbackTimer);
        setLoading(false);
      }
    });

    return () => {
      window.clearTimeout(fallbackTimer);
      unsubscribe();
    };
  }, []);

  if (checkoutStarting) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#fbf8f3] text-[#092723]">
        <div className="rounded-3xl border border-[#d7e5df] bg-white p-8 shadow-sm">
          Opening secure checkout...
        </div>
      </main>
    );
  }

  return (
    <>
      <StaleVersionGuard />
      <WorkContinuity />
      {user ? <Dashboard user={user} /> : <LandingPage />}
    </>
  );
}
